// Typed client for the authoritative backend (Django Ninja). PRODUCTION-DEFAULT: the game uses the
// authoritative backend (real gem economy, real gacha, persistence, leaderboards, ghosts) whenever
// it's reachable — auto-detected by probeBackend() (GET /api/health) at boot. If the backend is
// down / static-only hosting, it gracefully falls back to the offline demo. Explicit override:
// `?backend=1|0` (URL) or localStorage `jkgame.backend='1'|'0'`. Dev proxies '/api' → :8078.

// Optimistic until the boot probe resolves (a few hundred ms): assume backend present so a deployed
// site is "real" immediately; a failed call still degrades gracefully, and the probe flips this off
// for static-only hosting within the boot window.
let _backendUp = true
let _probed = false

/** Probe the backend once at boot. Sets the auto-detect flag isBackendEnabled() returns by default.
 *  Short timeout so static-only hosting falls back to the offline demo quickly. */
export async function probeBackend(): Promise<boolean> {
  try {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), 2500)
    const r = await fetch(`${BASE}/health`, { signal: ctrl.signal, cache: 'no-store' })
    clearTimeout(timer)
    _backendUp = r.ok
  } catch {
    _backendUp = false
  }
  _probed = true
  return _backendUp
}

/** Whether the authoritative backend drives the game. Explicit override (URL / localStorage) wins;
 *  otherwise the auto-detected availability (production = on when reachable). */
export function isBackendEnabled(): boolean {
  try {
    const q = new URLSearchParams(location.search)
    const v = q.get('backend')
    if (v === '1') return true
    if (v === '0') return false
    const ls = localStorage.getItem('jkgame.backend')
    if (ls === '1') return true
    if (ls === '0') return false
  } catch {
    /* location/localStorage unavailable → fall through to auto-detect */
  }
  return _backendUp
}

/** True once the boot probe has resolved (callers can wait if they need a definitive answer). */
export function backendProbed(): boolean {
  return _probed
}

export interface BackendWeapon {
  name: string
  kind: string
  power: number
  ammo: number
  range: string
}

export interface BackendPlayer {
  id: string
  name: string
  zone: number
  hp: number
  mhp: number
  sp: number
  msp: number
  rage: number
  kills: number
  has_key: boolean
  alive: boolean
  injuries: string[]
  weapon: BackendWeapon
}

export interface BackendOccupant {
  id: string
  name: string
  archetype: string
  zone: number
  hp: number
  mhp: number
  alive: boolean
  has_key: boolean
  is_ghost?: boolean // async-PvP ghost (a recorded rival run replayed as an opponent)
  char?: string
}

export interface BackendState {
  id: string
  status: 'running' | 'over'
  win_reason: 'survivor' | 'escape' | 'dead' | null
  step: number
  weather: number
  steps_to_shrink: number
  forbidden: number[]
  next_forbidden: number[]
  pending_target_id: string | null
  alive: number
  total: number
  rank: number
  player: BackendPlayer
  here: BackendOccupant[]
  entities: BackendOccupant[]
  pending_boon: string[] | null // three-choice run-buff offer (after a kill), or null
  boons: string[] // run buffs taken this match
}

export interface BackendAction {
  type: 'move' | 'begin' | 'attack' | 'special' | 'flee' | 'rest' | 'escape' | 'choose_boon'
  zone?: number
  target_id?: string
  kind?: 'sleep' | 'heal'
  boon_id?: string
}

const BASE = '/api'

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(BASE + url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!r.ok) throw new Error(`POST ${url} → ${r.status}`)
  return r.json() as Promise<T>
}

export async function apiNewMatch(seed: number, bots = 41, char?: string | null, ranked = false, handle?: string | null): Promise<{ id: string; state: BackendState }> {
  // handle lets a ranked match load OTHER players' ghosts on this seed (excluding your own).
  return postJson('/match/new', { seed, bots, char: char ?? null, ranked, handle: handle ?? null })
}

export async function apiGetMatch(id: string): Promise<BackendState> {
  const r = await fetch(`${BASE}/match/${id}`)
  if (!r.ok) throw new Error(`GET /match/${id} → ${r.status}`)
  return r.json() as Promise<BackendState>
}

export async function apiAction(id: string, action: BackendAction): Promise<{ result: unknown; state: BackendState }> {
  return postJson(`/match/${id}/action`, action)
}
